import type { Directive, DirectiveBinding } from "vue";

type TooltipPlacement =
    | "top"
    | "top-left"
    | "top-right"
    | "bottom"
    | "bottom-left"
    | "bottom-right"
    | "left"
    | "left-top"
    | "left-bottom"
    | "right"
    | "right-top"
    | "right-bottom";

type TooltipValue = string | null | undefined;

interface TooltipHTMLElement extends HTMLElement {
    _tooltipValue?: TooltipValue;
    _tooltipPlacement?: TooltipPlacement;
    _tooltipOffset?: number;
    _tooltipElement?: HTMLDivElement | null;
    _showTooltip?: () => void;
    _hideTooltip?: () => void;
}

/**
 * Парсит аргумент директивы для извлечения placement и offset
 * Примеры:
 * - "right:10" -> { placement: "right", offset: 10 }
 * - "right" -> { placement: "right", offset: 1 }
 * - "10" -> { placement: "bottom", offset: 10 }
 * - undefined -> { placement: "bottom", offset: 1 }
 */
function parseDirectiveArg(arg: string | undefined): { placement: TooltipPlacement; offset: number } {
    const defaultPlacement: TooltipPlacement = "bottom";
    const defaultOffset = 1;

    if (!arg) {
        return { placement: defaultPlacement, offset: defaultOffset };
    }

    const parts = arg.split(":");

    // Если одна часть
    if (parts.length === 1) {
        const part = parts[0];
        // Проверяем, является ли это числом
        const numValue = parseInt(part, 10);
        if (!isNaN(numValue)) {
            return { placement: defaultPlacement, offset: numValue };
        }
        // Иначе это placement
        return { placement: part as TooltipPlacement, offset: defaultOffset };
    }

    // Если две части: placement:offset
    if (parts.length === 2) {
        const placement = parts[0] as TooltipPlacement;
        const offset = parseInt(parts[1], 10);
        return {
            placement,
            offset: isNaN(offset) ? defaultOffset : offset
        };
    }

    return { placement: defaultPlacement, offset: defaultOffset };
}

export default {
    mounted(el: TooltipHTMLElement, binding: DirectiveBinding<TooltipValue>) {
        // Текст тултипа и его расположение.
        el._tooltipValue = binding.value;
        const { placement, offset } = parseDirectiveArg(binding.arg);
        el._tooltipPlacement = placement;
        el._tooltipOffset = offset;

        el._showTooltip = () => {
            if (!el._tooltipValue) return;

            const tooltip = document.createElement("div");
            const isHTML = /<\/?[a-z][\s\S]*>/i.test(el._tooltipValue);

            // Проверяем, есть ли HTML тэги. Если есть, используем innerHTML, иначе textContent
            if (isHTML) {
                tooltip.innerHTML = el._tooltipValue;
            } else {
                tooltip.textContent = el._tooltipValue;
            }

            tooltip.style.position = "absolute";
            tooltip.style.padding = isHTML ? "10px 14px" : "5px 8px";
            tooltip.style.background = "#FFF";
            tooltip.style.color = "#373D3F";
            tooltip.style.borderRadius = "8px";
            tooltip.style.zIndex = "999999";
            tooltip.style.fontSize = "12px";
            tooltip.style.fontWeight = "400";
            tooltip.style.boxShadow = "1px 2px 10px 0px #131E2726";
            tooltip.style.visibility = "hidden";
            tooltip.style.whiteSpace = "pre-wrap";
            tooltip.style.maxWidth = "290px";
            tooltip.style.lineHeight = "1.4";

            // Чтобы italic и bold корректно отображались внутри tooltip
            const italicElements = tooltip.querySelectorAll("i, em");
            italicElements.forEach((el: Element) => {
                (el as HTMLElement).style.fontStyle = "italic";
                (el as HTMLElement).style.fontSize = "inherit";
            });
            const boldElements = tooltip.querySelectorAll("b, strong");
            boldElements.forEach((el: Element) => {
                (el as HTMLElement).style.fontWeight = "500";
                (el as HTMLElement).style.fontSize = "inherit";
            });

            // Устанавливаем корректное расстояние между параграфами
            const paragraphElements = tooltip.querySelectorAll("p");
            paragraphElements.forEach((el: Element, index: number) => {
                (el as HTMLElement).style.margin = "0";
                (el as HTMLElement).style.fontSize = "inherit";
                (el as HTMLElement).style.fontWeight = "inherit";
                (el as HTMLElement).style.lineHeight = "inherit";
                if (index > 0) {
                    (el as HTMLElement).style.marginTop = "6px";
                }
            });

            document.body.appendChild(tooltip);
            el._tooltipElement = tooltip;

            // Расположение и смещение для тултипа.
            const rect = el.getBoundingClientRect();
            const ttRect = tooltip.getBoundingClientRect();
            const offset = el._tooltipOffset ?? 1;
            let top, left;

            switch (el._tooltipPlacement) {
                case "top":
                    top = window.scrollY + rect.top - ttRect.height - offset;
                    left = window.scrollX + rect.left + (rect.width - ttRect.width) / 2;
                    break;
                case "top-left":
                    top = window.scrollY + rect.top - ttRect.height - offset;
                    left = window.scrollX + rect.left;
                    break;
                case "top-right":
                    top = window.scrollY + rect.top - ttRect.height - offset;
                    left = window.scrollX + rect.right - ttRect.width;
                    break;
                case "bottom-left":
                    top = window.scrollY + rect.bottom + offset;
                    left = window.scrollX + rect.left;
                    break;
                case "bottom-right":
                    top = window.scrollY + rect.bottom + offset;
                    left = window.scrollX + rect.right - ttRect.width;
                    break;
                case "left-top":
                    top = window.scrollY + rect.top;
                    left = window.scrollX + rect.left - ttRect.width - offset;
                    break;
                case "left-bottom":
                    top = window.scrollY + rect.bottom - ttRect.height;
                    left = window.scrollX + rect.left - ttRect.width - offset;
                    break;
                case "right-top":
                    top = window.scrollY + rect.top;
                    left = window.scrollX + rect.right + offset;
                    break;
                case "right-bottom":
                    top = window.scrollY + rect.bottom - ttRect.height;
                    left = window.scrollX + rect.right + offset;
                    break;
                case "left":
                    top = window.scrollY + rect.top + (rect.height - ttRect.height) / 2;
                    left = window.scrollX + rect.left - ttRect.width - offset;
                    break;
                case "right":
                    top = window.scrollY + rect.top + (rect.height - ttRect.height) / 2;
                    left = window.scrollX + rect.right + offset;
                    break;
                case "bottom":
                default:
                    top = window.scrollY + rect.bottom + offset;
                    left = window.scrollX + rect.left + (rect.width - ttRect.width) / 2;
            }

            tooltip.style.top = `${top}px`;
            tooltip.style.left = `${left}px`;
            tooltip.style.visibility = "visible";
        };

        el._hideTooltip = () => {
            if (el._tooltipElement) {
                document.body.removeChild(el._tooltipElement);
                el._tooltipElement = null;
            }
        };

        el.addEventListener("mouseenter", el._showTooltip);
        el.addEventListener("mouseleave", el._hideTooltip);
        el.addEventListener("click", el._hideTooltip, true);
    },

    updated(el: TooltipHTMLElement, binding: DirectiveBinding<TooltipValue>) {
        el._tooltipValue = binding.value;
        const { placement, offset } = parseDirectiveArg(binding.arg);
        el._tooltipPlacement = placement;
        el._tooltipOffset = offset;

        // Пересоздание текущего тултипа при обновлении.
        if (el._tooltipElement && el._hideTooltip) {
            el._hideTooltip();
            // Если курсор находится до сих пор на элементе, то отображаем.
            if (el.matches(":hover") && el._showTooltip) {
                setTimeout(() => {
                    el._showTooltip?.();
                }, 0);
            }
        }
    },

    unmounted(el: TooltipHTMLElement) {
        if (el._showTooltip) {
            el.removeEventListener("mouseenter", el._showTooltip);
        }
        if (el._hideTooltip) {
            el.removeEventListener("mouseleave", el._hideTooltip);
            el.removeEventListener("click", el._hideTooltip, true);
        }
        if (el._tooltipElement) {
            document.body.removeChild(el._tooltipElement);
            el._tooltipElement = null;
        }
        delete el._showTooltip;
        delete el._hideTooltip;
        delete el._tooltipValue;
        delete el._tooltipPlacement;
        delete el._tooltipOffset;
    }
} as Directive<TooltipHTMLElement, TooltipValue>;
